import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Clock, StopCircle, AlertTriangle, Timer } from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import DateTimePicker from '@/components/Form/DateTimePicker'
import { SLADowntimeLog } from '@/modules/Service/types/sla'
import { formatFromUTC } from '@/utils/function'

dayjs.extend(utc)
dayjs.extend(timezone)

interface EndDowntimeModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (endTime: string) => void
    downtime: SLADowntimeLog | null
    isLoading?: boolean
    resolutionTimeMinutes?: number // SLA resolution time in minutes
}

interface FormData {
    downtime_end: dayjs.Dayjs
}

// Format minutes to readable string
const formatMinutes = (minutes: number) => {
    if (minutes < 60) return `${minutes} phút`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} giờ`
}

const EndDowntimeModal: React.FC<EndDowntimeModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    downtime,
    isLoading = false,
    resolutionTimeMinutes,
}) => {
    const { t } = useTranslation('services')

    const { control, handleSubmit, reset, watch } = useForm<FormData>({
        defaultValues: {
            downtime_end: dayjs(),
        },
    })

    const selectedEndTime = watch('downtime_end')

    // Reset form when modal opens with new downtime
    React.useEffect(() => {
        if (isOpen && downtime) {
            reset({
                downtime_end: dayjs(),
            })
        }
    }, [isOpen, downtime, reset])

    const onSubmit = (data: FormData) => {
        onConfirm(data.downtime_end.toISOString())
    }

    if (!downtime) return null

    const startTime = formatFromUTC(downtime.downtime_start)
    const startDayjs = dayjs.utc(downtime.downtime_start).tz()

    // Calculate SLA deadline if resolution time is set
    const slaDeadline = resolutionTimeMinutes
        ? startDayjs.add(resolutionTimeMinutes, 'minute')
        : null

    // Check if selected time exceeds SLA
    const exceedsSLA =
        slaDeadline && selectedEndTime && selectedEndTime.isAfter(slaDeadline)

    // Calculate how much time exceeded
    const exceededMinutes = exceedsSLA
        ? selectedEndTime.diff(slaDeadline, 'minute')
        : 0

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <StopCircle className="w-5 h-5 text-orange-500" />
                        {t('sla.end_downtime_title', 'Kết thúc Downtime')}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* Current downtime info */}
                    <div className="p-3 bg-gray-50 rounded-lg space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-600">
                                {t('sla.started_at', 'Bắt đầu lúc')}:
                            </span>
                            <span className="font-medium">{startTime}</span>
                        </div>
                        {downtime.downtime_description && (
                            <p className="text-gray-500 text-xs">
                                {downtime.downtime_description}
                            </p>
                        )}
                    </div>

                    {/* SLA Resolution Time Info */}
                    {resolutionTimeMinutes && slaDeadline && (
                        <div className="p-3 bg-blue-50 rounded-lg space-y-1.5 text-sm border border-blue-200">
                            <div className="flex items-center gap-2">
                                <Timer className="w-4 h-4 text-blue-600" />
                                <span className="text-blue-700 font-medium">
                                    {t('sla.resolution_sla', 'SLA Xử lý')}:{' '}
                                    {formatMinutes(resolutionTimeMinutes)}
                                </span>
                            </div>
                            <div className="text-xs text-blue-600">
                                {t(
                                    'sla.should_complete_by',
                                    'Cần hoàn thành trước'
                                )}
                                :{' '}
                                <span className="font-semibold">
                                    {slaDeadline.format('DD/MM/YYYY HH:mm')}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* End time picker */}
                    <div className="space-y-2">
                        <Label>{t('sla.end_time', 'Thời gian kết thúc')}</Label>
                        <DateTimePicker
                            control={control}
                            name="downtime_end"
                            minDate={startDayjs}
                            required
                        />
                    </div>

                    {/* SLA Exceeded Warning */}
                    {exceedsSLA && (
                        <div className="p-3 bg-red-50 rounded-lg border border-red-200 space-y-1">
                            <div className="flex items-center gap-2 text-red-700">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="font-medium text-sm">
                                    {t('sla.exceeded_warning', 'Vượt quá SLA!')}
                                </span>
                            </div>
                            <p className="text-xs text-red-600">
                                {t(
                                    'sla.exceeded_by',
                                    'Thời gian xử lý vượt quá SLA'
                                )}{' '}
                                <span className="font-bold">
                                    {formatMinutes(exceededMinutes)}
                                </span>
                            </p>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('cancel', 'Hủy')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className={
                                exceedsSLA
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-orange-600 hover:bg-orange-700'
                            }
                        >
                            {isLoading
                                ? t('processing', 'Đang xử lý...')
                                : t('sla.confirm_end', 'Xác nhận kết thúc')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default EndDowntimeModal
